// ============================================
// LIGA AJUPAM - Storage Service
// ============================================
// File upload handling for Supabase Storage and AWS S3

import { supabase, handleSupabaseError } from './client';
import { withTimeout } from '../../utils/withTimeout';
import type { UploadResult } from '../../types';

/**
 * Buckets de Storage disponibles (Supabase - legacy)
 */
export const STORAGE_BUCKETS = {
  AVATARS: 'avatars', // Público
  PAYMENT_RECEIPTS: 'payment-receipts', // Privado
  MATCH_PHOTOS: 'match-photos', // Privado
} as const;

/**
 * AWS S3 Buckets (nueva infraestructura)
 */
export const S3_BUCKETS = {
  AVATARS: 'liga-ajupam-avatars', // Público
  MATCH_PHOTOS: 'liga-ajupam-match-photos', // Privado
  RECEIPTS: 'liga-ajupam-receipts', // Privado
} as const;

/**
 * AWS S3 Region
 */
export const S3_REGION = 'us-east-2';

/**
 * Feature flag para usar S3 en lugar de Supabase Storage
 * Activado: AWS S3 configurado (2026-01-20)
 */
export const USE_S3_STORAGE = {
  AVATARS: true,
  MATCH_PHOTOS: true,
  RECEIPTS: true,
} as const;

/**
 * Límites de tamaño de archivo (en bytes)
 */
const FILE_SIZE_LIMITS = {
  AVATAR: 5 * 1024 * 1024, // 5MB
  RECEIPT: 5 * 1024 * 1024, // 5MB
  // v6.9.6: subido de 10 → 25MB. Fotos modernas de iPhone Pro / Android flagship
  // suelen pesar 10-18MB sin comprimir; el límite de 10MB hacía fallar en silencio
  // (en mobile el toast a veces no se ve por el reload del WebView).
  MATCH_PHOTO: 25 * 1024 * 1024, // 25MB
};

/**
 * Tipos de archivo permitidos.
 * v6.9.6: agregados image/heic e image/heif (formato nativo de iPhone)
 * y también string vacío (algunos pickers de Android no setean MIME).
 */
const ALLOWED_FILE_TYPES = {
  IMAGE: ['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'image/heic', 'image/heif', ''],
  IMAGE_AND_PDF: ['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'image/heic', 'image/heif', 'application/pdf', ''],
};

/**
 * Validar archivo antes de subir.
 * v6.9.6: si file.type viene vacío (común en algunos pickers móviles), se valida
 * por la extensión del nombre. Esto evita rechazos silenciosos en mobile.
 */
const validateFile = (
  file: File,
  maxSize: number,
  allowedTypes: string[]
): void => {
  // Validar tamaño
  if (file.size > maxSize) {
    const maxSizeMB = (maxSize / (1024 * 1024)).toFixed(1);
    throw new Error(`El archivo excede el tamaño máximo de ${maxSizeMB}MB`);
  }

  // Validar tipo. Si está vacío, fallback a extensión.
  const mime = file.type || '';
  const allowsImages = allowedTypes.some((t) => t.startsWith('image/'));
  const allowsPdf = allowedTypes.includes('application/pdf');
  const ext = (file.name.split('.').pop() || '').toLowerCase();
  const imgExt = ['jpg', 'jpeg', 'png', 'webp', 'heic', 'heif'].includes(ext);
  const pdfExt = ext === 'pdf';

  // Aceptamos por MIME explícito (incluyendo HEIC y string vacío) O por extensión.
  const okByMime = allowedTypes.includes(mime) || (mime.startsWith('image/') && allowsImages);
  const okByExt = (imgExt && allowsImages) || (pdfExt && allowsPdf);

  if (!okByMime && !okByExt) {
    const errorMsg = allowsPdf
      ? 'Tipo de archivo no permitido. Solo se permiten imágenes (JPG, PNG, WEBP, HEIC) o PDF.'
      : 'Tipo de archivo no permitido. Solo se permiten imágenes (JPG, PNG, WEBP, HEIC).';
    throw new Error(errorMsg);
  }
};

/**
 * Generar nombre único para archivo
 * Asegura que la extensión coincida con el tipo MIME
 */
const generateUniqueFileName = (originalName: string, mimeType: string): string => {
  const timestamp = Date.now();
  const randomStr = Math.random().toString(36).substring(2, 9);

  // Mapear MIME type a extensión correcta
  const extensionMap: Record<string, string> = {
    'image/jpeg': 'jpg',
    'image/jpg': 'jpg',
    'image/png': 'png',
    'image/webp': 'webp',
    'application/pdf': 'pdf',
  };

  const extension = extensionMap[mimeType] || originalName.split('.').pop() || 'jpg';
  return `${timestamp}-${randomStr}.${extension}`;
};

/**
 * v6.9.6: Compresión client-side antes de subir fotos del juez de cancha.
 *
 * Por qué importa: fotos de cámara mobile son 4-15MB. Subir esto sobre WiFi
 * débil de la cancha tarda 10-30 segundos, ventana en la que Android Chrome
 * puede evictar la pestaña por presión de memoria (especialmente después de
 * cerrar la app de cámara que consume mucha RAM). Si la pestaña es evictada
 * mid-upload, el wizard se "resetea" y la foto se pierde.
 *
 * Estrategia: usar createImageBitmap (más liviano que img.src+FileReader),
 * downscale a max 1920px, exportar JPEG 0.85. Reducción típica 5-10x.
 *
 * Fail-safe: si createImageBitmap falla (HEIC no soportado en Android Chrome
 * viejo, archivo corrupto, etc.), devolvemos el File original — el upload
 * sigue funcionando, sólo sin compresión.
 */
const compressImageIfPossible = async (file: File): Promise<File | Blob> => {
  // No comprimir si ya es chico (< 500KB) — overhead no vale la pena
  if (file.size < 500 * 1024) return file;

  // No comprimir PDFs ni nada que no sea imagen
  const isImage = file.type.startsWith('image/') || file.type === '';
  if (!isImage) return file;

  let bitmap: ImageBitmap | null = null;
  try {
    bitmap = await createImageBitmap(file);

    const maxDim = 1920; // max width O height
    const scale = Math.min(1, maxDim / Math.max(bitmap.width, bitmap.height));
    const w = Math.round(bitmap.width * scale);
    const h = Math.round(bitmap.height * scale);

    const canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext('2d');
    if (!ctx) return file;
    ctx.drawImage(bitmap, 0, 0, w, h);

    const blob = await new Promise<Blob | null>((resolve) => {
      canvas.toBlob(resolve, 'image/jpeg', 0.85);
    });

    if (!blob) return file;

    // Si la compresión no mejoró tamaño, mantener original
    if (blob.size >= file.size) return file;

    console.log(`📸 Foto comprimida: ${(file.size / 1024).toFixed(0)}KB → ${(blob.size / 1024).toFixed(0)}KB`);
    return blob;
  } catch (e) {
    // Típicamente HEIC en Android Chrome viejo, o memoria insuficiente
    console.warn('[compressImageIfPossible] No se pudo comprimir, subimos original:', (e as Error)?.message);
    return file;
  } finally {
    bitmap?.close?.();
  }
};

/**
 * Subir avatar de perfil
 * Bucket: avatars (público)
 */
export const uploadAvatar = async (
  file: File,
  userId: string
): Promise<UploadResult> => {
  try {
    // Validar archivo
    validateFile(file, FILE_SIZE_LIMITS.AVATAR, ALLOWED_FILE_TYPES.IMAGE);

    // Generar nombre único basado en el archivo original
    const fileName = generateUniqueFileName(file.name, file.type);
    const filePath = `${userId}/${fileName}`;

    // Convertir File a ArrayBuffer para evitar problemas de FormData
    const arrayBuffer = await file.arrayBuffer();

    // Upload usando ArrayBuffer
    const { data, error } = await supabase.storage
      .from(STORAGE_BUCKETS.AVATARS)
      .upload(filePath, arrayBuffer, {
        upsert: true,
        contentType: file.type,
      });

    if (error) throw handleSupabaseError(error);
    if (!data) throw new Error('No se pudo subir el archivo');

    // Obtener URL pública
    const { data: urlData } = supabase.storage
      .from(STORAGE_BUCKETS.AVATARS)
      .getPublicUrl(data.path);

    return {
      url: urlData.publicUrl,
      path: data.path,
    };
  } catch (error: any) {
    console.error('Error uploading avatar:', error);
    throw error;
  }
};

/**
 * Subir comprobante de pago
 * Bucket: payment-receipts (privado)
 */
export const uploadPaymentReceipt = async (
  file: File,
  teamId: string
): Promise<UploadResult> => {
  try {
    // Validar archivo (permitir imágenes y PDF)
    validateFile(file, FILE_SIZE_LIMITS.RECEIPT, ALLOWED_FILE_TYPES.IMAGE_AND_PDF);

    // Generar nombre único
    const fileName = `${teamId}/${generateUniqueFileName(file.name, file.type)}`;

    console.log('📤 Uploading receipt:', {
      fileName,
      type: file.type,
      size: `${(file.size / 1024).toFixed(2)}KB`,
    });

    // Convertir File a ArrayBuffer para evitar problemas de FormData
    const arrayBuffer = await file.arrayBuffer();

    // Subir usando ArrayBuffer
    const { data, error } = await supabase.storage
      .from(STORAGE_BUCKETS.PAYMENT_RECEIPTS)
      .upload(fileName, arrayBuffer, {
        cacheControl: '3600',
        upsert: false,
        contentType: file.type,
      });

    if (error) throw handleSupabaseError(error);
    if (!data) throw new Error('No se pudo subir el archivo');

    // Generar URL firmada (válida por 365 días para comprobantes de pago)
    const { data: signedData, error: signedError } = await supabase.storage
      .from(STORAGE_BUCKETS.PAYMENT_RECEIPTS)
      .createSignedUrl(data.path, 31536000); // 365 días en segundos

    if (signedError) throw handleSupabaseError(signedError);
    if (!signedData) throw new Error('No se pudo generar URL firmada');

    return {
      url: signedData.signedUrl,
      path: data.path,
    };
  } catch (error: any) {
    console.error('Error uploading receipt:', error);
    throw error;
  }
};

/**
 * Subir foto de resultado de partido
 * Bucket: match-photos (privado)
 * v6.2.6: Recorte inteligente a 16:9 con detección de jugadores
 */
export const uploadMatchPhoto = async (
  file: File,
  matchId: string
): Promise<UploadResult> => {
  try {
    // Validar archivo
    validateFile(file, FILE_SIZE_LIMITS.MATCH_PHOTO, ALLOWED_FILE_TYPES.IMAGE);

    // PASO 1: Recortar foto a formato 16:9 landscape con detección de jugadores
    let fileToUpload = file;

    try {
      console.log('🏐 Recortando foto de partido a formato 16:9...');

      // Importar dinámicamente para evitar problemas de bundling
      const { cropMatchPhotoToLandscape } = await import('../../utils/matchPhotoCrop');
      const cropResult = await cropMatchPhotoToLandscape(file);

      if (cropResult.success && cropResult.croppedFile) {
        fileToUpload = cropResult.croppedFile;
        console.log(`✅ Foto recortada: ${cropResult.facesDetected || 0} jugador(es) detectado(s)`);
      } else {
        console.warn('⚠️ No se pudo recortar la foto, usando imagen original');
      }
    } catch (cropError) {
      console.error('Error en recorte de foto (continuando con original):', cropError);
      // Continuar con la imagen original si falla el recorte
    }

    // PASO 2: Generar nombre único
    const fileName = `${matchId}/${generateUniqueFileName(fileToUpload.name, fileToUpload.type)}`;

    console.log('📤 Uploading match photo:', {
      fileName,
      type: fileToUpload.type,
      size: `${(fileToUpload.size / 1024).toFixed(2)}KB`,
    });

    // PASO 3: Convertir File a ArrayBuffer para evitar problemas de FormData
    const arrayBuffer = await fileToUpload.arrayBuffer();

    // PASO 4: Subir usando ArrayBuffer
    const { data, error } = await supabase.storage
      .from(STORAGE_BUCKETS.MATCH_PHOTOS)
      .upload(fileName, arrayBuffer, {
        cacheControl: '3600',
        upsert: false,
        contentType: fileToUpload.type,
      });

    if (error) throw handleSupabaseError(error);
    if (!data) throw new Error('No se pudo subir el archivo');

    // Generar URL firmada (válida por 365 días)
    const { data: signedData, error: signedError } = await supabase.storage
      .from(STORAGE_BUCKETS.MATCH_PHOTOS)
      .createSignedUrl(data.path, 31536000); // 365 días en segundos

    if (signedError) throw handleSupabaseError(signedError);
    if (!signedData) throw new Error('No se pudo generar URL firmada');

    return {
      url: signedData.signedUrl,
      path: data.path,
    };
  } catch (error: any) {
    console.error('Error uploading match photo:', error);
    throw error;
  }
};

/**
 * Obtener URL firmada (signed URL) para archivos privados
 * Válida por 1 hora
 */
export const getSignedUrl = async (
  bucket: string,
  path: string,
  expiresIn: number = 3600
): Promise<string> => {
  try {
    const { data, error } = await supabase.storage
      .from(bucket)
      .createSignedUrl(path, expiresIn);

    if (error) throw handleSupabaseError(error);
    if (!data) throw new Error('No se pudo generar URL firmada');

    return data.signedUrl;
  } catch (error: any) {
    console.error('Error getting signed URL:', error);
    throw error;
  }
};

/**
 * Eliminar archivo de storage
 */
export const deleteFile = async (bucket: string, path: string): Promise<void> => {
  try {
    const { error } = await supabase.storage.from(bucket).remove([path]);

    if (error) throw handleSupabaseError(error);
  } catch (error: any) {
    console.error('Error deleting file:', error);
    throw error;
  }
};

/**
 * Listar archivos de un directorio (admin)
 */
export const listFiles = async (bucket: string, path: string): Promise<any[]> => {
  try {
    const { data, error } = await supabase.storage.from(bucket).list(path);

    if (error) throw handleSupabaseError(error);

    return data || [];
  } catch (error: any) {
    console.error('Error listing files:', error);
    throw error;
  }
};

// ============================================
// AWS S3 FUNCTIONS
// ============================================

/**
 * Interfaz para respuesta de presigned URL
 */
interface PresignedUrlResponse {
  success: boolean;
  uploadUrl?: string;
  downloadUrl?: string;
  publicUrl?: string;
  key?: string;
  error?: string;
}

/**
 * Obtener presigned URL para subir a S3
 * Llama a la Edge Function get-s3-presigned-url
 */
export const getS3PresignedUrl = async (
  bucket: string,
  key: string,
  contentType: string,
  operation: 'GET' | 'PUT' = 'PUT'
): Promise<PresignedUrlResponse> => {
  try {
    // Timeout: la edge function de presigned alimenta el submit de foto/registro.
    const { data, error } = await withTimeout(
      supabase.functions.invoke('get-s3-presigned-url', {
        body: {
          bucket,
          key,
          contentType,
          operation,
          expiresIn: 3600, // 1 hora para upload
        },
      }),
      15000,
      'get-s3-presigned-url',
    );

    if (error) {
      console.error('Error getting presigned URL:', error);
      throw new Error(error.message || 'Error obteniendo URL de subida');
    }

    return data as PresignedUrlResponse;
  } catch (error: any) {
    console.error('Error in getS3PresignedUrl:', error);
    throw error;
  }
};

/**
 * Subir archivo a S3 usando presigned URL
 */
export const uploadToS3 = async (
  file: File,
  bucket: string,
  key: string
): Promise<{ url: string; key: string }> => {
  try {
    // 1. Obtener presigned URL para upload
    const presignedData = await getS3PresignedUrl(bucket, key, file.type, 'PUT');

    if (!presignedData.success || !presignedData.uploadUrl) {
      throw new Error(presignedData.error || 'No se pudo obtener URL de subida');
    }

    console.log('📤 Uploading to S3:', {
      bucket,
      key,
      type: file.type,
      size: `${(file.size / 1024).toFixed(2)}KB`,
    });

    // 2. Subir directamente a S3 — con timeout (60s, holgado para fotos grandes en
    //    mobile lento). Sin esto, un PUT colgado dejaba el submit de registro/foto
    //    "pensando" para siempre. AbortController aborta el fetch al vencer.
    const uploadAbort = new AbortController();
    const uploadTimer = setTimeout(() => uploadAbort.abort(), 60000);
    let uploadResponse: Response;
    try {
      uploadResponse = await fetch(presignedData.uploadUrl, {
        method: 'PUT',
        body: file,
        headers: {
          'Content-Type': file.type,
        },
        signal: uploadAbort.signal,
      });
    } catch (e: any) {
      if (e?.name === 'AbortError') {
        throw new Error('La subida del archivo tardó demasiado. Revisá tu conexión e intentá de nuevo.');
      }
      throw e;
    } finally {
      clearTimeout(uploadTimer);
    }

    if (!uploadResponse.ok) {
      const errorText = await uploadResponse.text();
      console.error('S3 upload failed:', uploadResponse.status, errorText);
      throw new Error(`Error al subir archivo: ${uploadResponse.status}`);
    }

    const publicUrl = presignedData.publicUrl || `https://${bucket}.s3.${S3_REGION}.amazonaws.com/${key}`;

    // v6.9.6: La verificación HEAD fue eliminada.
    // Para buckets PRIVADOS, la Edge Function devuelve una presigned GET URL como publicUrl.
    // Un HEAD a esa URL falla con 403 (el método no coincide con la firma) y lanzaba
    // una excepción que descartaba el upload aunque el PUT hubiera sido exitoso.
    // El PUT retornando ok=true (HTTP 200) es prueba suficiente de escritura exitosa en S3.
    console.log('✅ S3 upload successful:', key);

    return {
      url: publicUrl,
      key: presignedData.key || key,
    };
  } catch (error: any) {
    console.error('Error uploading to S3:', error);
    throw error;
  }
};

/**
 * Subir avatar a S3
 * Bucket: liga-ajupam-avatars (público)
 */
export const uploadAvatarToS3 = async (
  file: File,
  userId: string
): Promise<UploadResult> => {
  try {
    // Validar archivo
    validateFile(file, FILE_SIZE_LIMITS.AVATAR, ALLOWED_FILE_TYPES.IMAGE);

    // Generar key único
    const extension = file.name.split('.').pop() || 'jpg';
    const key = `${userId}/${Date.now()}-${Math.random().toString(36).substring(2, 9)}.${extension}`;

    // Subir a S3
    const result = await uploadToS3(file, S3_BUCKETS.AVATARS, key);

    return {
      url: result.url,
      path: result.key,
    };
  } catch (error: any) {
    console.error('Error uploading avatar to S3:', error);
    throw error;
  }
};

/**
 * Subir foto de partido a S3
 * Bucket: liga-ajupam-match-photos (privado)
 *
 * v6.9.6: comprime client-side antes de subir (ver compressImageIfPossible).
 * Fotos de cámara de 8-15MB se reducen a 500KB-2MB, lo que:
 *  - acorta el upload de 15-30s a 2-5s (crítico en WiFi de cancha)
 *  - reduce la ventana en la que Android Chrome puede evictar la pestaña
 *  - baja la presión de memoria del WebView en iOS PWA
 */
export const uploadMatchPhotoToS3 = async (
  file: File,
  matchId: string
): Promise<UploadResult> => {
  try {
    // Validar archivo (antes de comprimir — los límites son sobre el original)
    validateFile(file, FILE_SIZE_LIMITS.MATCH_PHOTO, ALLOWED_FILE_TYPES.IMAGE);

    // Comprimir antes de subir (fail-safe: devuelve original si falla)
    const processed = await compressImageIfPossible(file);

    // Si comprimimos, generamos un File con extensión .jpg y MIME image/jpeg
    const uploadFile = processed instanceof File
      ? processed
      : new File([processed], `${Date.now()}.jpg`, { type: 'image/jpeg' });

    // Generar key único — usar extensión del archivo a subir (jpg si fue comprimido)
    const extension = uploadFile.name.split('.').pop() || 'jpg';
    const key = `${matchId}/${Date.now()}-${Math.random().toString(36).substring(2, 9)}.${extension}`;

    // Subir a S3
    const result = await uploadToS3(uploadFile, S3_BUCKETS.MATCH_PHOTOS, key);

    return {
      url: result.url,
      path: result.key,
    };
  } catch (error: any) {
    console.error('Error uploading match photo to S3:', error);
    throw error;
  }
};

/**
 * Subir comprobante de pago a S3
 * Bucket: liga-ajupam-receipts (privado)
 */
export const uploadReceiptToS3 = async (
  file: File,
  teamId: string
): Promise<UploadResult> => {
  try {
    // Validar archivo (permitir imágenes y PDF)
    validateFile(file, FILE_SIZE_LIMITS.RECEIPT, ALLOWED_FILE_TYPES.IMAGE_AND_PDF);

    // Generar key único
    const extension = file.name.split('.').pop() || 'jpg';
    const key = `${teamId}/${Date.now()}-${Math.random().toString(36).substring(2, 9)}.${extension}`;

    // Subir a S3
    const result = await uploadToS3(file, S3_BUCKETS.RECEIPTS, key);

    return {
      url: result.url,
      path: result.key,
    };
  } catch (error: any) {
    console.error('Error uploading receipt to S3:', error);
    throw error;
  }
};

/**
 * Obtener URL pública de S3 (para buckets públicos)
 */
export const getS3PublicUrl = (bucket: string, key: string): string => {
  return `https://${bucket}.s3.${S3_REGION}.amazonaws.com/${key}`;
};

/**
 * Obtener URL firmada de S3 (para buckets privados)
 */
export const getS3SignedUrl = async (
  bucket: string,
  key: string,
  _expiresIn: number = 3600
): Promise<string> => {
  try {
    const presignedData = await getS3PresignedUrl(bucket, key, '', 'GET');

    if (!presignedData.success || !presignedData.downloadUrl) {
      throw new Error(presignedData.error || 'No se pudo obtener URL de descarga');
    }

    return presignedData.downloadUrl;
  } catch (error: any) {
    console.error('Error getting S3 signed URL:', error);
    throw error;
  }
};

/**
 * Verificar si una URL de S3 está expirada
 * Revisa el parámetro X-Amz-Date y X-Amz-Expires
 */
export const isS3UrlExpired = (url: string): boolean => {
  try {
    if (!url.includes('X-Amz-Expires')) {
      return false; // No es URL firmada de S3, no expira
    }

    const urlObj = new URL(url);
    const dateParam = urlObj.searchParams.get('X-Amz-Date');
    const expiresParam = urlObj.searchParams.get('X-Amz-Expires');

    if (!dateParam || !expiresParam) {
      return false;
    }

    // X-Amz-Date formato: 20260130T210118Z
    const year = parseInt(dateParam.substring(0, 4));
    const month = parseInt(dateParam.substring(4, 6)) - 1;
    const day = parseInt(dateParam.substring(6, 8));
    const hour = parseInt(dateParam.substring(9, 11));
    const minute = parseInt(dateParam.substring(11, 13));
    const second = parseInt(dateParam.substring(13, 15));

    const signedDate = new Date(Date.UTC(year, month, day, hour, minute, second));
    const expiresInSeconds = parseInt(expiresParam);

    // AWS S3 rechaza URLs con expiración > 7 días (604800 segundos)
    // Considerar estas URLs como expiradas para forzar regeneración (fix v6.2.22)
    if (expiresInSeconds > 604800) {
      console.log(`⚠️ S3 URL con expiración inválida detectada: ${expiresInSeconds}s (máx: 604800s)`);
      return true;
    }

    const expirationDate = new Date(signedDate.getTime() + expiresInSeconds * 1000);

    return new Date() >= expirationDate;
  } catch (error) {
    console.error('Error checking S3 URL expiration:', error);
    return true; // En caso de error, asumir expirada para regenerar
  }
};

/**
 * Extraer bucket y key de una URL completa de S3
 * Ejemplo: https://liga-ajupam-receipts.s3.us-east-2.amazonaws.com/team-123/file.pdf?X-Amz...
 * Retorna: { bucket: 'liga-ajupam-receipts', key: 'team-123/file.pdf' }
 */
export const parseS3Url = (url: string): { bucket: string; key: string } | null => {
  try {
    const urlObj = new URL(url);

    // Formato: bucket-name.s3.region.amazonaws.com
    const hostParts = urlObj.hostname.split('.');
    if (hostParts.length < 4 || !hostParts.includes('s3') || !hostParts.includes('amazonaws')) {
      return null;
    }

    const bucket = hostParts[0];
    const key = urlObj.pathname.substring(1); // Remover '/' inicial

    return { bucket, key };
  } catch (error) {
    return null;
  }
};

/**
 * Regenerar URL de S3 si está expirada
 * Si la URL es de S3 y está expirada, genera una nueva
 * Si no es de S3 o no está expirada, retorna la URL original
 */
export const refreshS3UrlIfExpired = async (url: string): Promise<string> => {
  try {
    // Verificar si es URL de S3
    if (!url.includes('s3') || !url.includes('amazonaws.com')) {
      return url; // No es S3, retornar original
    }

    // Verificar si está expirada
    if (!isS3UrlExpired(url)) {
      return url; // No está expirada, retornar original
    }

    console.log('🔄 S3 URL expirada, regenerando...');

    // Extraer bucket y key
    const parsed = parseS3Url(url);
    if (!parsed) {
      console.error('No se pudo parsear URL de S3:', url);
      return url; // Retornar original si no se puede parsear
    }

    // Regenerar URL firmada
    const newUrl = await getS3SignedUrl(parsed.bucket, parsed.key);
    console.log('✅ URL regenerada exitosamente');

    return newUrl;
  } catch (error) {
    console.error('Error regenerando URL de S3:', error);
    return url; // En caso de error, retornar original
  }
};

/**
 * v6.10.0.31 — URL ESTABLE de proxy para fotos de bucket privado (match-photos).
 *
 * Reescribe una presigned URL (o key) de S3 a la edge function `proxy-s3-image`,
 * que firma el GET server-side en cada request. La URL resultante NUNCA expira,
 * así que reemplaza a `refreshS3UrlIfExpired` para MOSTRAR fotos: es sincrónica
 * (sin await, sin red) y se puede usar directo en `<img src>`, `background-image`
 * o `fetch()` (canvas/descarga).
 *
 * - Proxyea `match-photos` (privado, vence) Y `avatars`. Los avatares son
 *   públicos pero su URL CRUDA de S3 no manda CORS → html-to-image no los puede
 *   inlinar al canvas y las fotos de las parejas "desaparecían" en las placas.
 *   El proxy responde `Access-Control-Allow-Origin: *` y URL estable. Cualquier
 *   otro bucket/URL se devuelve sin cambios.
 * - Idempotente: una URL de proxy ya armada no parsea como S3 → se devuelve igual.
 *
 * Solución permanente al bug de presigned URL expirada + al de fotos de avatar
 * que no cargaban en las placas (ver lección S3_PRESIGNED_URL_EXPIRY_BUG_v6_3_8).
 */
export const s3ImageProxyUrl = (stored?: string | null): string => {
  if (!stored) return '';
  const ref = parseS3Url(stored);
  if (!ref || (ref.bucket !== S3_BUCKETS.MATCH_PHOTOS && ref.bucket !== S3_BUCKETS.AVATARS)) return stored;
  const base = (import.meta.env.VITE_SUPABASE_URL as string | undefined)?.replace(/\/+$/, '');
  if (!base) return stored;
  return `${base}/functions/v1/proxy-s3-image?bucket=${ref.bucket}&key=${encodeURIComponent(ref.key)}`;
};

// ============================================
// WRAPPER FUNCTIONS (Unified API)
// ============================================

/**
 * Subir avatar (selecciona automáticamente Supabase o S3 según configuración)
 */
export const uploadAvatarUnified = async (
  file: File,
  userId: string
): Promise<UploadResult> => {
  if (USE_S3_STORAGE.AVATARS) {
    return uploadAvatarToS3(file, userId);
  }
  return uploadAvatar(file, userId);
};

/**
 * Subir foto de partido (selecciona automáticamente Supabase o S3 según configuración)
 */
export const uploadMatchPhotoUnified = async (
  file: File,
  matchId: string
): Promise<UploadResult> => {
  if (USE_S3_STORAGE.MATCH_PHOTOS) {
    return uploadMatchPhotoToS3(file, matchId);
  }
  return uploadMatchPhoto(file, matchId);
};

/**
 * Subir comprobante de pago (selecciona automáticamente Supabase o S3 según configuración)
 */
export const uploadReceiptUnified = async (
  file: File,
  teamId: string
): Promise<UploadResult> => {
  if (USE_S3_STORAGE.RECEIPTS) {
    return uploadReceiptToS3(file, teamId);
  }
  return uploadPaymentReceipt(file, teamId);
};

/**
 * Subir imagen/documento de evidencia para suplencia manual iniciada por admin.
 * Bucket: substitution-evidence (público, sin validación facial).
 */
export const uploadSubstitutionEvidence = async (
  file: File
): Promise<UploadResult> => {
  const ext = file.name.split('.').pop()?.toLowerCase() || 'bin';
  const path = `${crypto.randomUUID()}.${ext}`;

  validateFile(file, 20 * 1024 * 1024, ALLOWED_FILE_TYPES.IMAGE_AND_PDF);

  const { data, error } = await supabase.storage
    .from('substitution-evidence')
    .upload(path, file, { contentType: file.type, upsert: false });

  if (error) throw new Error(`Error subiendo evidencia: ${error.message}`);

  const { data: urlData } = supabase.storage
    .from('substitution-evidence')
    .getPublicUrl(data.path);

  return { url: urlData.publicUrl, path: data.path };
};
